// Microbe RTS — combat resolution: the fully-connected RPS-5 pentagon.
//
//   perHit = max(1, dmg * counterMult - targetArmor) * (1 + flank)
//   counterMult applies when the attacker is STRONG vs the target (COUNTERS table).
//   Toxifer applies stacking DoT + splash instead of a direct hit.
//
// All numbers come from config.COMBAT / COUNTERS / ECON. Colors/vfx are not our concern.

import type { GameState, Unit, WarriorClass, Side } from '../types.js';
import { COMBAT, COUNTERS, ECON, WORLD, WILD, QUEEN, BASE_DEFENSE } from '../config.js';
import { dist, depthAt, unitSpec, isNonCombat, queenDmg } from './world.js';
// Runtime-only cycle (units.ts imports effectiveTarget/attackRange from here) — safe: both are
// called at tick time, never at module init.
import { classDmgMult } from './units.js';

// ---------------------------------------------------------------------------
// Targeting (shared by movement + combat so approach and attack agree).
// ---------------------------------------------------------------------------
function isHostile(a: Unit, b: Unit): boolean {
  return b.hp > 0 && b.side !== a.side;
}

/**
 * What a WILD grazer should act on: the nearest colony unit that has entered its territory (a disc
 * around its guard point) — or, once it's been provoked, anything still within its leash. Grazers
 * ignore each other and never leave to hunt. Provocation is (re)armed here so it keeps committing.
 */
function wildTarget(state: GameState, g: Unit): Unit | null {
  const guard = g.guardPos ?? g.pos;
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const e of state.units) {
    if (e.hp <= 0 || e.side === 'wild') continue; // only you/bot units, never other grazers
    const dGuard = dist(guard, e.pos);
    const inTerritory = dGuard <= WILD.territoryRadius;
    const chasing = g.provoke > 0 && dGuard <= WILD.leashRadius;
    if (!inTerritory && !chasing) continue;
    const d = dist(g.pos, e.pos);
    if (d < bestD) { bestD = d; best = e; }
  }
  if (best) g.provoke = WILD.provokeSeconds; // seeing/holding a target keeps it angry
  return best;
}

/** The unit a given unit should be acting on this tick (explicit focus or auto-aggro). */
export function effectiveTarget(state: GameState, u: Unit): Unit | null {
  if (u.hp <= 0) return null;
  if (u.side === 'wild') return wildTarget(state, u);

  // Workers + healers never auto-engage (healer has zero attack).
  if (isNonCombat(u.kind)) return null;

  // Explicit focus-fire order wins while the target is alive + a different alliance (lets you focus
  // a rival unit OR deliberately farm a wild grazer).
  if (u.focusId) {
    const t = findUnit(state, u.focusId);
    if (t && isHostile(u, t)) return t;
  }

  // 'hold' units — and units sieging a node — only bite what's already adjacent; others sweep the full
  // aggro radius. The siege case is load-bearing: it stops ranged units from halting at range on a base
  // defender (which would leave them outside the ~50px core doing zero erosion), without touching the
  // player's chosen bandStance.
  const aggro =
    u.bandStance === 'hold' || u.attackNodeId != null
      ? u.radius + COMBAT.reach + 24
      : COMBAT.aggroRadius;
  // Wild grazers are only fought back at point-blank (retaliation) — your army never auto-charges a
  // tanky neutral across the map. To farm one you FOCUS it explicitly (handled above).
  const retaliate = u.radius + COMBAT.reach + 24;

  let best: Unit | null = null;
  let bestD = Infinity;
  for (const e of state.units) {
    if (!isHostile(u, e)) continue;
    const reach = e.side === 'wild' ? retaliate : aggro;
    const d = dist(u.pos, e.pos);
    if (d <= reach && d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

function findUnit(state: GameState, id: string): Unit | undefined {
  for (const u of state.units) if (u.id === id) return u;
  return undefined;
}

/** Attack range for `a` vs `b` (ranged uses spec.range + light bonus; melee uses radii + reach). */
export function attackRange(a: Unit, b: Unit): number {
  const spec = unitSpec(a.kind);
  if (spec.range > 0) {
    const lit = depthAt(a.pos) <= WORLD.bands.photic[1];
    return spec.range + (lit ? COMBAT.rangedLightBonus : 0);
  }
  return a.radius + b.radius + COMBAT.reach;
}

// ---------------------------------------------------------------------------
// Main resolve.
// ---------------------------------------------------------------------------
export function resolveCombat(state: GameState, dt: number): void {
  // 1. Damage-over-time ticks (toxifer stacks) + wild aggression decay.
  for (const u of state.units) {
    if (u.hp <= 0) continue;
    if (u.provoke > 0) u.provoke = Math.max(0, u.provoke - dt);
    if (u.dotStacks > 0) {
      // Toxifer damage is DoT, not a direct hit — scale it by the APPLIER's genome dmg boost so
      // Toxifer/'all' damage nodes affect poison too (applyHit never sees this path).
      const dotGm = u.dotSide === 'you' || u.dotSide === 'bot' ? classDmgMult(state.colonies[u.dotSide], 'toxifer') : 1;
      const dd = COMBAT.toxiferDotPerStack * u.dotStacks * dotGm * dt;
      u.hp -= dd;
      // A grazer whittled down purely by DoT still farms amino for whoever applied the stacks
      // (otherwise toxifer-killing a grazer would silently pay nothing).
      if (u.side === 'wild') creditWildDamage(state, u.dotSide, dd, u.hp <= 0);
      u.dotTimer -= dt;
      if (u.dotTimer <= 0) {
        u.dotStacks = 0;
        u.dotTimer = 0;
        u.dotSide = null;
      }
    }
  }

  // 2. Resolve one attack per unit whose cooldown is ready and target in range.
  //    Precompute per-target attacker counts for the flank bonus.
  const targetOf = new Map<string, Unit>();
  const attackersOn = new Map<string, number>();
  for (const u of state.units) {
    if (u.hp <= 0 || isNonCombat(u.kind)) continue; // workers + healers deal no damage
    const t = effectiveTarget(state, u);
    if (t) {
      targetOf.set(u.id, t);
      attackersOn.set(t.id, (attackersOn.get(t.id) ?? 0) + 1);
    }
  }

  for (const u of state.units) {
    if (u.hp <= 0) continue;
    u.cooldown = Math.max(0, u.cooldown - dt);
    const t = targetOf.get(u.id);
    if (!t || t.hp <= 0) continue;
    const spec = unitSpec(u.kind);
    if (dist(u.pos, t.pos) > attackRange(u, t)) continue;
    if (u.cooldown > 0) continue;
    u.cooldown = spec.interval;

    if (u.kind === 'toxifer') {
      applyToxifer(state, u, t);
    } else {
      applyHit(state, u, t, attackersOn.get(t.id) ?? 0);
    }
  }

  // 3. Remove the dead + clean references.
  cleanupDead(state);
}

function counterMultFor(attacker: Unit, target: Unit): number {
  const strong = COUNTERS[attacker.kind as WarriorClass];
  return strong && strong.includes(target.kind as WarriorClass) ? COMBAT.counterMult : 1;
}

function inFriendlyPOI(state: GameState, u: Unit): boolean {
  for (const n of state.nodes) {
    if (n.owner !== u.side) continue;
    if (dist(u.pos, n.pos) <= n.radius) return true;
  }
  return false;
}

function applyHit(state: GameState, a: Unit, b: Unit, attackersOnTarget: number): void {
  const bSpec = unitSpec(b.kind);
  // Visible strike: ranged attackers (the stinger) leave a fading tracer to the target, reusing the
  // ranged-defense shot system that render already fades + drains.
  if (unitSpec(a.kind).range > 0 && (a.side === 'you' || a.side === 'bot')) {
    state.shots.push({ from: { ...a.pos }, to: { ...b.pos }, ttl: BASE_DEFENSE.shotTtl, side: a.side });
  }
  const cover = inFriendlyPOI(state, b);
  const armor = bSpec.armor + (b.queen ? QUEEN.armor : 0) + (cover ? COMBAT.poiArmorBonus : 0);

  // Genome damage boost (Queen excluded — queenDmg already carries QUEEN.dmgMult).
  const gm = !a.queen && (a.side === 'you' || a.side === 'bot') ? classDmgMult(state.colonies[a.side], a.kind) : 1;
  let dmg = Math.max(1, queenDmg(a) * counterMultFor(a, b) * gm - armor);
  if (attackersOnTarget >= COMBAT.flankMinAttackers) dmg *= 1 + COMBAT.flankBonus;
  if (cover) dmg *= 1 - COMBAT.poiDamageReduction;

  b.hp -= dmg;
  if (b.side === 'wild') farmWild(state, a, b, dmg); // graze it for amino + wake it up
  if (b.hp <= 0) creditSalvage(state, a.side, b);
}

/** Attacking a wild grazer FARMS amino (per damage dealt, + a lump on the kill) and provokes it. */
function farmWild(state: GameState, attacker: Unit, grazer: Unit, dmg: number): void {
  grazer.provoke = WILD.provokeSeconds;
  creditWildDamage(state, attacker.side, dmg, grazer.hp <= 0);
}

/** Credit a colony amino for damage dealt to a grazer (shared by direct hits + DoT ticks). */
function creditWildDamage(state: GameState, side: Unit['side'] | null, dmg: number, killed: boolean): void {
  if (side !== 'you' && side !== 'bot') return;
  const col = state.colonies[side];
  col.res.amino += dmg * WILD.aminoPerDamage;
  if (killed) col.res.amino += WILD.aminoOnKill;
}

function addStack(u: Unit, bySide: Unit['side']): void {
  u.dotStacks = Math.min(COMBAT.toxiferDotMaxStacks, u.dotStacks + 1);
  u.dotTimer = COMBAT.toxiferDotDuration;
  u.dotSide = bySide;
}
function applyToxifer(state: GameState, a: Unit, primary: Unit): void {
  addStack(primary, a.side);
  if (primary.side === 'wild') primary.provoke = WILD.provokeSeconds;
  // Splash: stack nearby enemies of the toxifer.
  for (const e of state.units) {
    if (e === primary || e.hp <= 0 || e.side === a.side) continue;
    if (dist(primary.pos, e.pos) <= COMBAT.toxiferSplashRadius) {
      addStack(e, a.side);
      if (e.side === 'wild') e.provoke = WILD.provokeSeconds;
    }
  }
}

function creditSalvage(state: GameState, killerSide: Unit['side'], dead: Unit): void {
  if (killerSide !== 'you' && killerSide !== 'bot') return; // wild banks nothing
  const cost = unitSpec(dead.kind).cost;
  const col = state.colonies[killerSide];
  col.res.energy += cost.energy * ECON.salvageEnergyFrac;
  col.res.amino += cost.amino * ECON.salvageAminoFrac;
}

/** Remove hp<=0 units and scrub their ids from nodes/selection/groups/focus. */
export function cleanupDead(state: GameState): void {
  let anyDead = false;
  for (const u of state.units) {
    if (u.hp <= 0) {
      anyDead = true;
      break;
    }
  }
  if (!anyDead) return;

  const dead = new Set<string>();
  const alive: Unit[] = [];
  for (const u of state.units) {
    if (u.hp <= 0) dead.add(u.id);
    else alive.push(u);
  }
  state.units = alive;

  for (const n of state.nodes) {
    if (n.assigned.length) n.assigned = n.assigned.filter((id) => !dead.has(id));
  }
  // Prune the dead from BOTH sides' selection + control groups (presentation state, per side).
  for (const side of ['you', 'bot'] as Side[]) {
    state.selectionBySide[side] = state.selectionBySide[side].filter((id) => !dead.has(id));
    state.groupsBySide[side] = state.groupsBySide[side].map((g) => g.filter((id) => !dead.has(id)));
  }
  for (const u of state.units) {
    if (u.focusId && dead.has(u.focusId)) u.focusId = null;
  }
}
